import React, { useState, useEffect } from 'react';
import Footer from '../Footer';
import './styles/MenuPage.css';

const MenuPage = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
  const [selectedWeek, setSelectedWeek] = useState('current');
  const [selectedDietType, setSelectedDietType] = useState(null);
  const [weekMenus, setWeekMenus] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const dietCategories = [
    { id: 'normal', name: 'Normál', icon: '🍽️' },
    { id: 'diabetic', name: 'Diabetikus', icon: '🩺' },
    { id: 'soft', name: 'Pépes', icon: '🥄' },
    { id: 'gentle', name: 'Kímélő', icon: '🌿' },
    { id: 'vital', name: 'Vitál', icon: '💪' },
    { id: 'dairy-free', name: 'Tejmentes', icon: '🥛' },
    { id: 'lactose-free', name: 'Laktózmentes', icon: '🚫' }
  ];

  const monthNames = [
    'Január', 'Február', 'Március', 'Április', 'Május', 'Június',
    'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December'
  ];

  const getCurrentWeekDates = () => {
    const today = new Date();
    const currentDay = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - currentDay + 1);

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      weekDates.push(date);
    }
    return weekDates;
  };

  const getNextWeekDates = () => {
    const currentWeek = getCurrentWeekDates();
    return currentWeek.map((date) => {
      const nextWeekDate = new Date(date);
      nextWeekDate.setDate(date.getDate() + 7);
      return nextWeekDate;
    });
  };

  const formatDateShort = (date) => `${monthNames[date.getMonth()]} ${date.getDate()}.`;

  const formatWeekRange = (dates) =>
    `${formatDateShort(dates[0])}\u00a0–\u00a0${formatDateShort(dates[6])}`;

  const currentWeekDates = getCurrentWeekDates();
  const nextWeekDates = getNextWeekDates();
  const menuHasPdf = (menu) =>
    Boolean(menu?.pdfFile?.filename || menu?.pdfFile?.path);

  const availableCategories = dietCategories.filter((category) =>
    weekMenus.some((menu) => menu.category === category.id && menuHasPdf(menu))
  );

  const currentMenu =
    weekMenus.find(
      (menu) => menu.category === selectedDietType && menuHasPdf(menu)
    ) || null;

  const selectedCategory =
    availableCategories.find((cat) => cat.id === selectedDietType) ||
    availableCategories[0] ||
    null;

  const pdfUrl = currentMenu ? `${API_BASE_URL}/menus/pdf/${currentMenu._id}` : null;

  useEffect(() => {
    loadWeekMenus();
  }, [selectedWeek]);

  useEffect(() => {
    if (availableCategories.length === 0) {
      setSelectedDietType(null);
      return;
    }
    const stillAvailable = availableCategories.some((cat) => cat.id === selectedDietType);
    if (!stillAvailable) {
      setSelectedDietType(availableCategories[0].id);
    }
  }, [weekMenus, selectedWeek]);

  const loadWeekMenus = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const currentYear = new Date().getFullYear();
      const response = await fetch(
        `${API_BASE_URL}/menus?week=${selectedWeek}&year=${currentYear}`
      );

      if (!response.ok) {
        throw new Error('Failed to load menus');
      }

      const data = await response.json();
      setWeekMenus(Array.isArray(data.menus) ? data.menus : []);
    } catch (err) {
      console.error('Failed to load menus:', err);
      setError('Nem sikerült betölteni az étlapokat');
      setWeekMenus([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="menu-page">
      <div className="menu-shell container">
        <header className="menu-page-header">
          <span className="menu-page-icon" aria-hidden>🍽️</span>
          <div>
            <h1>Heti Étlap</h1>
            <p>Válasszon hetet és étkezési típust az étlap megtekintéséhez</p>
          </div>
        </header>

        <div className="menu-controls-card">
          <div className="menu-control-row">
            <span className="menu-control-label">1. Hét</span>
            <div className="menu-segment" role="group" aria-label="Hét választása">
              <button
                type="button"
                className={`menu-segment-btn ${selectedWeek === 'current' ? 'active' : ''}`}
                onClick={() => setSelectedWeek('current')}
              >
                <span className="menu-segment-title">Ez a hét</span>
                <span className="menu-segment-sub">{formatWeekRange(currentWeekDates)}</span>
              </button>
              <button
                type="button"
                className={`menu-segment-btn ${selectedWeek === 'next' ? 'active' : ''}`}
                onClick={() => setSelectedWeek('next')}
              >
                <span className="menu-segment-title">Következő hét</span>
                <span className="menu-segment-sub">{formatWeekRange(nextWeekDates)}</span>
              </button>
            </div>
          </div>

          {availableCategories.length > 0 && (
            <div className="menu-control-row menu-control-row--diet">
              <span className="menu-control-label">2. Étkezési típus</span>
              <div className="menu-diet-grid" role="group" aria-label="Étkezési típus">
                {availableCategories.map((category) => (
                  <button
                    type="button"
                    key={category.id}
                    className={`menu-diet-btn ${selectedDietType === category.id ? 'active' : ''}`}
                    onClick={() => setSelectedDietType(category.id)}
                    title={category.name}
                  >
                    <span className="menu-diet-icon">{category.icon}</span>
                    <span className="menu-diet-name">{category.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="menu-main">
          <div className="menu-viewer">
            {isLoading && (
              <div className="menu-panel-state">
                <p>Étlap betöltése…</p>
              </div>
            )}

            {error && (
              <div className="menu-panel-state menu-panel-state--error">
                <p>{error}</p>
                <button type="button" onClick={loadWeekMenus} className="menu-retry-btn">
                  Újrapróbálás
                </button>
              </div>
            )}

            {!isLoading && !error && currentMenu && pdfUrl && (
              <>
                <iframe
                  src={`${pdfUrl}#page=1`}
                  title={`${selectedCategory?.name || ''} étlap`}
                  className="menu-pdf-frame"
                />
                <div className="menu-pdf-actions">
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="menu-action-btn menu-action-download"
                  >
                    Letöltés PDF-ben
                  </a>
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="menu-action-btn menu-action-view"
                  >
                    Teljes képernyős nézet
                  </a>
                </div>
              </>
            )}

            {!isLoading && !error && availableCategories.length === 0 && (
              <div className="menu-panel-state">
                <span className="menu-panel-state-icon" aria-hidden>📄</span>
                <h3>Nincs elérhető étlap</h3>
                <p>
                  A {selectedWeek === 'current' ? 'aktuális' : 'következő'} hétre még nincs feltöltött
                  étlap. Próbálja meg később, vagy válasszon másik hetet.
                </p>
              </div>
            )}

            {!isLoading && !error && availableCategories.length > 0 && !currentMenu && (
              <div className="menu-panel-state">
                <p>Válasszon étkezési típust az étlap megjelenítéséhez.</p>
              </div>
            )}
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MenuPage;
